import type IMonitoringAgentDefinition from './interfaces/IMonitoringAgentDefinition';

export const REPUTATION_MONITORING_AGENT: IMonitoringAgentDefinition = {
  sidebarId: 'public-sentiment-monitoring',
  sessionType: 'reputation-monitoring',
  subType: 'media-sentiment',
  titleKey: 'valueChainMap.monitoring.reputation.title',
  sessionTitleKey: 'valueChainMap.monitoring.reputation.sessionTitle',
  generatingLabelKey: 'valueChainMap.monitoring.reputation.generating',
  generatingDescriptionKey: 'valueChainMap.monitoring.reputation.generatingDescription',
  metricField: 'sentimentScore',
  metricLabelKey: 'valueChainMap.monitoring.reputation.metricLabel',
  sessionsInfoKey: 'valueChainMap.monitoring.reputation.sessionsInfo',
  schedulesInfoKey: 'valueChainMap.monitoring.reputation.schedulesInfo',
  selectionTitleKey: 'valueChainMap.monitoring.reputation.selectionTitle',
  selectionInfoKey: 'valueChainMap.monitoring.reputation.selectionInfo',
  selectionEmptyKey: 'valueChainMap.monitoring.reputation.selectionEmpty',
  startMonitoringKey: 'valueChainMap.monitoring.reputation.startMonitoring',
  continueToScheduleKey: 'valueChainMap.monitoring.reputation.continueToSchedule',
  selectionMode: 'upstream-suppliers',
  resultsMode: 'articles',
  impactTrigger: 'high-risk-count',
};

export const REGULATION_MONITORING_AGENT: IMonitoringAgentDefinition = {
  sidebarId: 'product-regulation-monitoring',
  sessionType: 'regulation-monitoring',
  subType: 'regulation-tracking',
  titleKey: 'valueChainMap.monitoring.regulation.title',
  sessionTitleKey: 'valueChainMap.monitoring.regulation.sessionTitle',
  generatingLabelKey: 'valueChainMap.monitoring.regulation.generating',
  generatingDescriptionKey: 'valueChainMap.monitoring.regulation.generatingDescription',
  metricField: 'impactLevel',
  metricLabelKey: 'valueChainMap.monitoring.regulation.metricLabel',
  sessionsInfoKey: 'valueChainMap.monitoring.regulation.sessionsInfo',
  schedulesInfoKey: 'valueChainMap.monitoring.regulation.schedulesInfo',
  selectionTitleKey: 'valueChainMap.monitoring.regulation.selectionTitle',
  selectionInfoKey: 'valueChainMap.monitoring.regulation.selectionInfo',
  selectionEmptyKey: 'valueChainMap.monitoring.regulation.selectionEmpty',
  startMonitoringKey: 'valueChainMap.monitoring.regulation.startMonitoring',
  continueToScheduleKey: 'valueChainMap.monitoring.regulation.continueToSchedule',
  selectionMode: 'products-mixed',
  resultsMode: 'regulations',
  impactTrigger: 'high-or-critical-impact',
};

export const FINANCE_MONITORING_AGENT: IMonitoringAgentDefinition = {
  sidebarId: 'financial-stability-monitoring',
  sessionType: 'finance-monitoring',
  subType: 'financial-risk',
  titleKey: 'valueChainMap.monitoring.finance.title',
  sessionTitleKey: 'valueChainMap.monitoring.finance.sessionTitle',
  generatingLabelKey: 'valueChainMap.monitoring.finance.generating',
  generatingDescriptionKey: 'valueChainMap.monitoring.finance.generatingDescription',
  metricField: 'stabilityScore',
  metricLabelKey: 'valueChainMap.monitoring.finance.metricLabel',
  sessionsInfoKey: 'valueChainMap.monitoring.finance.sessionsInfo',
  schedulesInfoKey: 'valueChainMap.monitoring.finance.schedulesInfo',
  selectionTitleKey: 'valueChainMap.monitoring.finance.selectionTitle',
  selectionInfoKey: 'valueChainMap.monitoring.finance.selectionInfo',
  selectionEmptyKey: 'valueChainMap.monitoring.finance.selectionEmpty',
  startMonitoringKey: 'valueChainMap.monitoring.finance.startMonitoring',
  continueToScheduleKey: 'valueChainMap.monitoring.finance.continueToSchedule',
  selectionMode: 'upstream-suppliers',
  resultsMode: 'financial-risk',
  impactTrigger: 'high-risk-count',
};

export const OWNERSHIP_MONITORING_AGENT: IMonitoringAgentDefinition = {
  sidebarId: 'ownership-monitoring',
  sessionType: 'ownership-monitoring',
  subType: 'ownership-monitoring',
  titleKey: 'valueChainMap.monitoring.ownership.title',
  sessionTitleKey: 'valueChainMap.monitoring.ownership.sessionTitle',
  generatingLabelKey: 'valueChainMap.monitoring.ownership.generating',
  generatingDescriptionKey: 'valueChainMap.monitoring.ownership.generatingDescription',
  metricField: 'contagionScore',
  metricLabelKey: 'valueChainMap.monitoring.ownership.metricLabel',
  sessionsInfoKey: 'valueChainMap.monitoring.ownership.sessionsInfo',
  schedulesInfoKey: 'valueChainMap.monitoring.ownership.schedulesInfo',
  selectionTitleKey: 'valueChainMap.monitoring.ownership.selectionTitle',
  selectionInfoKey: 'valueChainMap.monitoring.ownership.selectionInfo',
  selectionEmptyKey: 'valueChainMap.monitoring.ownership.selectionEmpty',
  startMonitoringKey: 'valueChainMap.monitoring.ownership.startMonitoring',
  continueToScheduleKey: 'valueChainMap.monitoring.ownership.continueToSchedule',
  selectionMode: 'upstream-suppliers',
  resultsMode: 'ownership',
  impactTrigger: 'high-risk-count',
};

export const CLIMATE_MONITORING_AGENT: IMonitoringAgentDefinition = {
  sidebarId: 'climate-monitoring',
  sessionType: 'climate-monitoring',
  subType: 'climate-risk',
  titleKey: 'valueChainMap.monitoring.climate.title',
  sessionTitleKey: 'valueChainMap.monitoring.climate.sessionTitle',
  generatingLabelKey: 'valueChainMap.monitoring.climate.generating',
  generatingDescriptionKey: 'valueChainMap.monitoring.climate.generatingDescription',
  metricField: 'exposureScore',
  metricLabelKey: 'valueChainMap.monitoring.climate.metricLabel',
  sessionsInfoKey: 'valueChainMap.monitoring.climate.sessionsInfo',
  schedulesInfoKey: 'valueChainMap.monitoring.climate.schedulesInfo',
  selectionTitleKey: 'valueChainMap.monitoring.climate.selectionTitle',
  selectionInfoKey: 'valueChainMap.monitoring.climate.selectionInfo',
  selectionEmptyKey: 'valueChainMap.monitoring.climate.selectionEmpty',
  startMonitoringKey: 'valueChainMap.monitoring.climate.startMonitoring',
  continueToScheduleKey: 'valueChainMap.monitoring.climate.continueToSchedule',
  selectionMode: 'climate-mixed',
  resultsMode: 'climate',
  impactTrigger: 'high-risk-count',
};

const MONITORING_AGENTS_BY_SIDEBAR_ID: Record<string, IMonitoringAgentDefinition> = {
  [REPUTATION_MONITORING_AGENT.sidebarId]: REPUTATION_MONITORING_AGENT,
  [REGULATION_MONITORING_AGENT.sidebarId]: REGULATION_MONITORING_AGENT,
  [FINANCE_MONITORING_AGENT.sidebarId]: FINANCE_MONITORING_AGENT,
  [OWNERSHIP_MONITORING_AGENT.sidebarId]: OWNERSHIP_MONITORING_AGENT,
  [CLIMATE_MONITORING_AGENT.sidebarId]: CLIMATE_MONITORING_AGENT,
};

const MONITORING_AGENTS_BY_SESSION_TYPE: Record<string, IMonitoringAgentDefinition> = {
  [REPUTATION_MONITORING_AGENT.sessionType]: REPUTATION_MONITORING_AGENT,
  [REGULATION_MONITORING_AGENT.sessionType]: REGULATION_MONITORING_AGENT,
  [FINANCE_MONITORING_AGENT.sessionType]: FINANCE_MONITORING_AGENT,
  [OWNERSHIP_MONITORING_AGENT.sessionType]: OWNERSHIP_MONITORING_AGENT,
  [CLIMATE_MONITORING_AGENT.sessionType]: CLIMATE_MONITORING_AGENT,
};

export const MONITORING_AGENT_REGISTRY: IMonitoringAgentDefinition[] = [
  REPUTATION_MONITORING_AGENT,
  REGULATION_MONITORING_AGENT,
  FINANCE_MONITORING_AGENT,
  OWNERSHIP_MONITORING_AGENT,
  CLIMATE_MONITORING_AGENT,
];

export const getMonitoringAgentBySidebarId = (
  sidebarId: string,
): IMonitoringAgentDefinition | undefined => (
  MONITORING_AGENTS_BY_SIDEBAR_ID[sidebarId]
);

export const getMonitoringAgentBySessionType = (
  sessionType: string,
): IMonitoringAgentDefinition | undefined => (
  MONITORING_AGENTS_BY_SESSION_TYPE[sessionType]
);

export const getMonitoringAgentByActiveKind = (
  activeAgentKind:
  | 'reputation-monitoring'
  | 'regulation-monitoring'
  | 'finance-monitoring'
  | 'ownership-monitoring'
  | 'climate-monitoring'
  | string
  | null
  | undefined,
): IMonitoringAgentDefinition => {
  const bySession = activeAgentKind
    ? MONITORING_AGENTS_BY_SESSION_TYPE[activeAgentKind]
    : undefined;

  if (bySession) {
    return bySession;
  }

  return REPUTATION_MONITORING_AGENT;
};
